import { GetServerSidePropsContext } from 'next';
import { useEffect } from 'react';
import Router, { useRouter } from 'next/router';
import { ScoutLocation } from '@/types/location';
import { getMonitoredLocations } from '@/api/location';
import Layout from '@/components/layout/Layout';
import Scout from '@/components/ui/scout/Scout';
import { useUser } from '@/hooks/useUser';
import ScoutCard from '@/components/ui/scout/ScoutCard';
import { Views } from '@/types/view';
import { ScoutProvider } from '@/hooks/useScout';
import UserStore from '@/utils/session';

interface Props {
  locations: ScoutLocation[];
}

const Index: React.FC<Props> = ({ locations }) => {
  const { user } = useUser();
  const router = useRouter();
  const { query } = router;

  const page = Array.isArray(query.id) ? query.id[0] : query.id;
  const isValid = Object.values(Views).some(validPage => validPage === page);

  return (
    <Layout>
      {user ? (
        <ScoutProvider locations={locations}>
          <Scout page={isValid ? (page as Views) : Views.List} />
        </ScoutProvider>
      ) : (
        <ScoutCard />
      )}
    </Layout>
  );
};

export const getServerSideProps = async (
  context: GetServerSidePropsContext,
) => {
  try {
    let sortedLocations;
    const user = UserStore.get();

    if (!user) {
      return {
        props: { locations: [] },
      };
    }

    const { locations, organization } = await getMonitoredLocations(
      user.currentOrganization,
      context.req,
    );

    if (organization) {
      // if an organization is returned (ie: was created), update the user's organizations and currentOrganization
      UserStore.set({
        ...user,
        organizations: [...user.organizations, organization],
        currentOrganization: organization._id,
      });
    }

    if (locations && locations.length > 0) {
      sortedLocations = locations.sort((a: ScoutLocation, b: ScoutLocation) => {
        const aDate = a.lastSeen ? new Date(a.lastSeen).getTime() : 0;
        const bDate = b.lastSeen ? new Date(b.lastSeen).getTime() : 0;
        return bDate - aDate;
      });
    }

    return {
      props: { locations: sortedLocations || [] },
    };
  } catch (error: any) {
    return {
      props: { locations: [] },
    };
  }
};

export default Index;
